import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { TabComponent } from '../../../../../../ui-components/lib/tabs/tab/tab.component';
import { TabsComponent } from '../../../../../../ui-components/lib/tabs/tabs.component';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { CustomWorkflowService } from '../custom-workflow.service';
import {
  CustomWorkflow,
  CustomWorkflowApiError,
  CustomWorkflowStatus,
  PublishError,
  PublishErrorCode,
} from '../custom-workflow.model';
import { CustomWorkflowPreviewComponent } from '../custom-workflow-preview/custom-workflow-preview.component';
import { CustomWorkflowCanvasTabComponent } from '../custom-workflow-canvas-tab/custom-workflow-canvas-tab.component';
import { CustomWorkflowFormEditorComponent } from '../custom-workflow-form-editor/custom-workflow-form-editor.component';
import { CustomWorkflowSettingsComponent } from '../custom-workflow-settings/custom-workflow-settings.component';

@Component({
  selector: 'lib-custom-workflow-detail',
  imports: [
    TranslocoPipe,
    BadgeComponent,
    ButtonComponent,
    OverlayLoaderComponent,
    TabsComponent,
    TabComponent,
    CustomWorkflowCanvasTabComponent,
    CustomWorkflowFormEditorComponent,
    CustomWorkflowSettingsComponent,
    CustomWorkflowPreviewComponent,
  ],
  templateUrl: './custom-workflow-detail.component.html',
  styleUrl: './custom-workflow-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomWorkflowDetailComponent implements OnInit {
  readonly service = inject(CustomWorkflowService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);
  private readonly transloco = inject(TranslocoService);
  private readonly toaster = inject(ToasterService);
  private readonly modalService = inject(ModalServiceService);
  private readonly destroyRef = inject(DestroyRef);

  readonly previewTemplate = viewChild<TemplateRef<unknown>>('preview');

  readonly secondaryBtn =
    'px-lg py-sm text-sm rounded-md text-neutral-700 bg-white font-medium border border-neutral-300';
  readonly destructiveBtn =
    'px-lg py-sm text-sm rounded-md text-destructive-700 bg-white font-medium border border-destructive-300';
  readonly primaryBtn =
    'px-lg py-sm text-sm rounded-md text-white bg-primary-500 font-medium border-none';
  readonly Status = CustomWorkflowStatus;

  id = 0;
  workflow = signal<CustomWorkflow | null>(null);
  publishErrors = signal<PublishError[]>([]);
  isBusy = signal(false);
  activeTabIndex = signal(0);

  title = computed(() => this.workflow()?.name ?? '');
  readonly = computed(() => this.workflow()?.kind === 'built_in');
  boxErrors = computed(() => ({
    noFields: this.hasError(PublishErrorCode.NoFields),
    nobodyCanSubmit: this.hasError(PublishErrorCode.NobodyCanSubmit),
  }));

  /** Duplicate navigates to the copy on this same route, so Angular reuses the component: follow the id. */
  ngOnInit() {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.onWorkflowIdChange(Number(params.get('id'))));
  }

  private onWorkflowIdChange(id: number) {
    this.id = id;
    this.activeTabIndex.set(0);
    this.publishErrors.set([]);
    this.load();
  }

  load() {
    this.service
      .show(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.workflow.set(res.data),
        error: () =>
          this.router.navigateByUrl(
            this.tenantService.adminPath(ADMIN_ROUTES.WORKFLOW.CUSTOM_WORKFLOWS)
          ),
      });
  }

  openPreview() {
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: '',
      customizeContent: this.previewTemplate(),
      width: '48',
      modalClass: 'overflow-y-auto',
    });
  }

  toggleActive() {
    const wf = this.workflow();
    if (!wf) return;
    const activating = !wf.is_active;
    this.isBusy.set(true);
    this.service
      .setActive(this.id, activating)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isBusy.set(false);
          this.publishErrors.set([]);
          if (activating) {
            this.toaster.add(
              this.transloco.translate('custom_workflow_published_toast'),
              '',
              '',
              'success',
              true,
              true
            );
          }
          this.load();
        },
        error: (e: CustomWorkflowApiError) => {
          this.isBusy.set(false);
          if (e.publish_errors) {
            this.publishErrors.set(e.publish_errors);
            this.toaster.add(
              e.message ?? '',
              e.publish_errors.map(p => p.message).join(' · '),
              '',
              'error',
              true,
              true
            );
            return;
          }
          if (activating && e.message) {
            this.toaster.add(e.message, '', '', 'error', true, true);
            return;
          }
          this.onApiError(e);
        },
      });
  }

  duplicate() {
    this.service
      .duplicate(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res =>
        this.router.navigateByUrl(
          this.tenantService.adminPath(
            `${ADMIN_ROUTES.WORKFLOW.CUSTOM_WORKFLOWS}/${res.data.id}`
          )
        )
      );
  }

  hasError(code: PublishErrorCode, stepId?: number): boolean {
    return this.publishErrors().some(
      e => e.code === code && (stepId === undefined || e.step_id === stepId)
    );
  }

  onApiError(error: CustomWorkflowApiError) {
    if (error?.locked) {
      this.load();
      return;
    }
    if (error?.errors && error.message) {
      this.toaster.add(error.message, '', '', 'error', true, true);
    }
  }

  onEditorChanged() {
    this.publishErrors.set([]);
    this.load();
  }
}
